<template>
  <!-- 主题选择下弹浮层：横向 dock 风格，一屏 3 个图标，左右滑动 -->
  <transition name="tp-slide">
    <div v-if="visible" class="tp-panel" @click.stop>
      <!-- 左滑动按钮 -->
      <button class="tp-arrow tp-arrow-left" @click="scrollBy(-1)">
        <i class="ri-arrow-left-s-line"></i>
      </button>

      <!-- dock 轨道：只展示 3 个 -->
      <div ref="track" class="tp-track">
        <div
          v-for="(t, i) in themes"
          :key="t.value"
          class="tp-dock-item"
          :class="{
            selected: currentTheme === t.value,
            'edge-fade': isEdgeFade(i)
          }"
          @click="pickTheme(t.value)"
        >
          <i :class="t.icon" class="tp-dock-icon"></i>
          <span class="tp-dock-label">{{ t.label }}</span>
          <i v-if="currentTheme === t.value" class="ri-check-line tp-dock-check"></i>
        </div>
      </div>

      <!-- 右滑动按钮 -->
      <button class="tp-arrow tp-arrow-right" @click="scrollBy(1)">
        <i class="ri-arrow-right-s-line"></i>
      </button>

      <!-- 海景背景开关（仅版画主题时显示） -->
      <template v-if="currentTheme === 'linocut'">
        <div class="tp-divider"></div>
        <div class="tp-sea-toggle" @click="$emit('toggle-sea-bg')">
          <i :class="seaBg ? 'ri-checkbox-circle-line' : 'ri-checkbox-blank-circle-line'"></i>
          <span>海景背景</span>
        </div>
        <div v-if="seaBg" class="tp-opacity-row">
          <span>透明度</span>
          <input
            type="range" min="0" max="0.3" step="0.02"
            :value="seaOpacity"
            @input="$emit('update:sea-opacity', parseFloat($event.target.value))"
          >
          <span class="tp-opacity-val">{{ Math.round(seaOpacity * 100) }}%</span>
        </div>
      </template>
    </div>
  </transition>
</template>

<script>
/**
 * ThemePicker — 横向 dock 风格主题选择浮层
 *
 * 锚定在主题按钮下方弹出，横向排列图标
 * 一屏展示 3 个，左右箭头滑动切换，边缘项淡入淡出
 * 毛玻璃深色风格，CSS 变量统一管理
 */
export default {
  name: 'ThemePicker',
  props: {
    visible: { type: Boolean, default: false },
    currentTheme: { type: String, default: 'light' },
    themes: { type: Array, default: () => [] },
    seaBg: { type: Boolean, default: false },
    seaOpacity: { type: Number, default: 0.12 }
  },
  data () {
    return {
      scrollIdx: 0
    }
  },
  watch: {
    visible (v) {
      if (v) {
        // 打开时滚动到当前选中项
        const idx = this.themes.findIndex(t => t.value === this.currentTheme)
        this.scrollIdx = Math.max(0, idx - 1)
        this.$nextTick(() => this.applyScroll())
      }
    }
  },
  methods: {
    pickTheme (value) {
      this.$emit('select', value)
      this.$emit('close')
    },
    scrollBy (dir) {
      const max = Math.max(0, this.themes.length - 3)
      this.scrollIdx = Math.min(max, Math.max(0, this.scrollIdx + dir))
      this.applyScroll()
    },
    applyScroll () {
      const track = this.$refs.track
      if (!track) return
      const item = track.querySelector('.tp-dock-item')
      if (!item) return
      const itemW = item.offsetWidth + 8
      track.scrollTo({ left: this.scrollIdx * itemW, behavior: 'smooth' })
    },
    // 判断某项是否在边缘（超出可视 3 个的范围），用于淡入淡出
    isEdgeFade (i) {
      return i < this.scrollIdx || i >= this.scrollIdx + 3
    }
  }
}
</script>

<style scoped>
/* ===== CSS 变量统一管理 ===== */
.tp-panel {
  --tp-bg: rgba(20, 23, 26, 0.82);
  --tp-border: rgba(255, 255, 255, 0.10);
  --tp-item-hover-bg: rgba(255, 255, 255, 0.08);
  --tp-item-selected-bg: rgba(79, 195, 247, 0.12);
  --tp-text: #E2E8F0;
  --tp-text-muted: #94A3B8;
  --tp-accent: var(--c-accent, #4FC3F7);
  --tp-radius: 14px;

  position: absolute;
  top: 44px;
  right: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  background: var(--tp-bg);
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
  border: 1px solid var(--tp-border);
  border-radius: var(--tp-radius);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.40);
  padding: 8px;
  z-index: 200;
}

/* 左右箭头按钮 */
.tp-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 48px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--tp-text-muted);
  font-size: 18px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}

.tp-arrow:hover {
  background: var(--tp-item-hover-bg);
  color: var(--tp-text);
}

/* dock 轨道 */
.tp-track {
  display: flex;
  gap: 8px;
  overflow: hidden;
  scroll-behavior: smooth;
  /* 隐藏滚动条 */
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.tp-track::-webkit-scrollbar { display: none; }

/* dock 条目 */
.tp-dock-item {
  flex-shrink: 0;
  width: 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 4px;
  border-radius: 10px;
  cursor: pointer;
  position: relative;
  background: transparent;
  color: var(--tp-text-muted);
  transition: background 0.2s ease, color 0.2s ease, opacity 0.3s ease, transform 0.3s ease;
}

.tp-dock-item:hover {
  background: var(--tp-item-hover-bg);
  color: var(--tp-text);
}

/* 选中态 */
.tp-dock-item.selected {
  background: var(--tp-item-selected-bg);
  color: var(--tp-accent);
}

.tp-dock-item.selected:hover {
  background: var(--tp-item-selected-bg);
}

/* dock 图标 */
.tp-dock-icon {
  font-size: 22px;
  transition: transform 0.2s ease;
}

.tp-dock-item:hover .tp-dock-icon {
  transform: scale(1.15);
}

.tp-dock-item.selected .tp-dock-icon {
  transform: scale(1.1);
}

/* dock 标签 */
.tp-dock-label {
  font-size: 11px;
  white-space: nowrap;
}

/* 勾选标记 */
.tp-dock-check {
  position: absolute;
  top: 2px;
  right: 2px;
  font-size: 12px;
  color: var(--tp-accent);
}

/* 边缘淡入淡出：超出可视范围的条目 */
.tp-dock-item.edge-fade {
  opacity: 0.15;
  transform: scale(0.85);
  pointer-events: none;
}

/* 分割线 */
.tp-divider {
  width: 1px;
  height: 40px;
  background: var(--tp-border);
  margin: 0 4px;
  align-self: center;
}

/* 海景背景开关 */
.tp-sea-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  color: var(--tp-text-muted);
  white-space: nowrap;
  transition: background 0.15s;
}

.tp-sea-toggle:hover {
  background: var(--tp-item-hover-bg);
}

.tp-sea-toggle i {
  color: var(--tp-accent);
  font-size: 15px;
}

/* 透明度滑杆 */
.tp-opacity-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  font-size: 12px;
  color: var(--tp-text-muted);
  white-space: nowrap;
}

.tp-opacity-row input[type="range"] {
  width: 60px;
  height: 4px;
  -webkit-appearance: none;
  appearance: none;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 2px;
  outline: none;
  cursor: pointer;
}

.tp-opacity-row input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--tp-accent);
  cursor: pointer;
}

.tp-opacity-row input[type="range"]::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border: none;
  border-radius: 50%;
  background: var(--tp-accent);
  cursor: pointer;
}

.tp-opacity-val {
  font-size: 11px;
  flex-shrink: 0;
}

/* 面板进出场：淡入淡出 + 轻微下滑 */
.tp-slide-enter-active,
.tp-slide-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.tp-slide-enter-from,
.tp-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* 移动端 */
@media (max-width: 560px) {
  .tp-panel {
    right: -8px;
  }
}
</style>
